<template>
  <div
    ref="footerEl"
    class="dialog__footer-wrapper tw-flex tw-shrink-0 tw-flex-col"
    :style="footerStyle"
  >
    <!-- Row 1: Footer Controls (optional) -->
    <div
      v-if="showFooterControls"
      class="footer-row-1 tw-flex tw-w-full tw-flex-wrap tw-items-center tw-gap-6 tw-border-t tw-border-gray-medium tw-bg-white tw-p-3"
    >
      <!-- Built-in Archived Toggle -->
      <modal-control-toggle
        v-if="showArchivedToggle"
        ref="archivedToggleRef"
        label="Archived"
        :model-value="includeArchived"
        :primary-color="primaryColor"
        hotkey="a"
        :aria-label="
          includeArchived ? 'Exclude archived items' : 'Include archived items'
        "
        @update:model-value="handleArchivedToggle"
      />

      <!-- Built-in None Option Toggle -->
      <modal-control-toggle
        v-if="showNoneOption"
        ref="noneOptionRef"
        :label="noneOptionText"
        :model-value="noneSelected"
        :primary-color="primaryColor"
        hotkey="n"
        aria-label="Toggle no item selection"
        @update:model-value="handleSelectNone"
      />

      <!-- Custom controls slot for modal-specific toggles -->
      <slot name="custom-controls" />
    </div>

    <!-- Row 2: Action Buttons (conditionally visible) -->
    <div
      v-if="showFooterActions"
      class="footer-row-2 dialog__footer tw-flex tw-shrink-0 tw-gap-3"
      @keydown.up.prevent="$emit('footer-up-navigation')"
      @keydown.down.prevent="$emit('footer-down-navigation')"
    >
      <!-- Custom action buttons slot (e.g., Delete button) - rendered before Cancel/Save -->
      <slot name="custom-actions" />

      <slot v-bind="slotProps">
        <p-btn
          v-if="!config.hideSecondaryAction"
          class="tw-flex-1 tw-border tw-border-gray-medium tw-bg-gray-light tw-text-gray-medium"
          flat
          :disabled="config.loading"
          @click="$emit('cancel')"
          @keydown.up.prevent="$emit('footer-up-navigation')"
          @keydown.down.prevent="$emit('footer-down-navigation')"
        >
          <span
            class="tw-flex tw-items-center tw-justify-center tw-gap-2 tw-text-gray-medium-dark"
          >
            <span>{{ secondaryActionLabel }}</span>
            <keyboard-shortcut-hint
              v-if="showSecondaryKeyboardHint"
              :shortcut="secondaryActionShortcut"
              theme="gray-medium"
              :aria-label="`${secondaryActionLabel} keyboard shortcut`"
            />
          </span>
        </p-btn>
        <p-btn
          ref="primaryButtonRef"
          :color="config.primaryColor"
          :disabled="!config.canSave || config.loading"
          :loading="config.loading"
          class="tw-flex-1"
          @click="$emit('save')"
          @keydown.enter.prevent="$emit('save')"
          @keydown.up.prevent="$emit('footer-up-navigation')"
          @keydown.down.prevent="$emit('footer-down-navigation')"
        >
          <span class="tw-flex tw-items-center tw-justify-center tw-gap-2">
            <span>{{ primaryActionLabel }}</span>
            <keyboard-shortcut-hint
              v-if="showKeyboardHint"
              :shortcut="primaryActionShortcut"
              :aria-label="`${primaryActionLabel} keyboard shortcut`"
            />
          </span>
        </p-btn>
      </slot>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, nextTick } from 'vue';
import { Colors } from '@/api';
import ModalControlToggle from './ModalControlToggle.vue';
import KeyboardShortcutHint from '@/components/core/KeyboardShortcutHint.vue';

export interface SharedModalFooterConfig {
  // Display
  primaryColor?: Colors;

  // Actions
  primaryActionText?: string;
  secondaryActionText?: string;
  primaryActionDisabled?: boolean;
  hideSecondaryAction?: boolean;

  // Footer Controls (Row 1)
  showFooterControls?: boolean;
  showArchivedToggle?: boolean;
  includeArchived?: boolean;
  showNoneOption?: boolean;
  noneOptionText?: string;
  noneSelected?: boolean;

  // Footer Actions (Row 2)
  showFooterActions?: boolean;

  // State
  canSave?: boolean;
  loading?: boolean;
}

interface Props {
  config: SharedModalFooterConfig;
}

interface Emits {
  (e: 'save', event?: Event): void;
  (e: 'cancel', event?: Event): void;
  (e: 'footer-up-navigation'): void;
  (e: 'footer-down-navigation'): void;
  (e: 'toggle-archived', value: boolean): void;
  (e: 'select-none', value: boolean): void;
}

const props = withDefaults(defineProps<Props>(), {
  config: () => ({}),
});

const emit = defineEmits<Emits>();

const primaryButtonRef =
  ref<InstanceType<typeof import('@/components/core/PBtn.vue').default>>();
const footerEl = ref<HTMLDivElement>();
const archivedToggleRef = ref<InstanceType<typeof ModalControlToggle>>();
const noneOptionRef = ref<InstanceType<typeof ModalControlToggle>>();

// Extract values from config with defaults
const primaryColor = computed(() => props.config.primaryColor || Colors.BLUE);
const showArchivedToggle = computed(
  () => props.config.showArchivedToggle || false
);
const includeArchived = computed(() => props.config.includeArchived || false);
const showNoneOption = computed(() => props.config.showNoneOption || false);
const noneOptionText = computed(() => props.config.noneOptionText || 'None');
const noneSelected = computed(() => props.config.noneSelected || false);
const showFooterControls = computed(
  () => props.config.showFooterControls ?? false
);
const showFooterActions = computed(
  () => props.config.showFooterActions ?? true
);

// Parse primary action text to extract label and shortcut
const primaryActionParsed = computed(() => {
  const text = props.config.primaryActionText || 'Save';
  // Check if text contains a shortcut pattern like "Label (Shortcut)"
  const match = text.match(/^(.+?)\s*\(([^)]+)\)$/);
  if (match) {
    return {
      label: match[1].trim(),
      shortcut: match[2].trim(),
      hasShortcut: true,
    };
  }
  return {
    label: text,
    shortcut: '',
    hasShortcut: false,
  };
});

const primaryActionLabel = computed(() => primaryActionParsed.value.label);
const primaryActionShortcut = computed(
  () => primaryActionParsed.value.shortcut
);
const showKeyboardHint = computed(() => primaryActionParsed.value.hasShortcut);

// Parse secondary action text to extract label and shortcut
const secondaryActionParsed = computed(() => {
  const text = props.config.secondaryActionText || 'Cancel';
  // Check if text contains a shortcut pattern like "Label (Shortcut)"
  const match = text.match(/^(.+?)\s*\(([^)]+)\)$/);
  if (match) {
    return {
      label: match[1].trim(),
      shortcut: match[2].trim(),
      hasShortcut: true,
    };
  }
  return {
    label: text,
    shortcut: '',
    hasShortcut: false,
  };
});

const secondaryActionLabel = computed(() => secondaryActionParsed.value.label);
const secondaryActionShortcut = computed(
  () => secondaryActionParsed.value.shortcut
);
const showSecondaryKeyboardHint = computed(
  () => secondaryActionParsed.value.hasShortcut
);

// CSS variables for theming
const footerStyle = computed(() => ({
  '--primary-color': `rgb(var(--tw-color-${
    props.config.primaryColor || Colors.BLUE
  }))`,
}));

// Computed props for slot
const slotProps = computed(() => ({
  canSave: props.config.canSave ?? true,
  isLoading: props.config.loading ?? false,
  handleSave: () => emit('save'),
  handleCancel: () => emit('cancel'),
}));

// Event handlers for toggles
function handleArchivedToggle(value: boolean) {
  emit('toggle-archived', value);
}

function handleSelectNone(value: boolean) {
  emit('select-none', value);
}

// Expose for parent access
defineExpose({
  primaryButtonRef,
  footerEl,
  $el: footerEl, // Expose as $el for backward compatibility
  focusPrimaryAction: async () => {
    // Return true if we successfully focused the primary action, false otherwise
    if (!props.config.showFooterActions) {
      return false;
    }

    // Wait for Vue to finish rendering
    await nextTick();

    if (!primaryButtonRef.value?.$el) {
      return false;
    }

    // Try to find the actual button element
    const button =
      primaryButtonRef.value.$el.querySelector('button') ||
      primaryButtonRef.value.$el;

    // Ensure the element is focusable and visible
    if (!button || typeof button.focus !== 'function') {
      return false;
    }

    if (!document.contains(button) || button.offsetParent === null) {
      return false;
    }

    // Wait for one animation frame to ensure the DOM is fully painted
    await new Promise((resolve) => requestAnimationFrame(resolve));

    // Focus the button
    button.focus();

    // Wait one more frame to let focus settle
    await new Promise((resolve) => requestAnimationFrame(resolve));

    // Return true if focus landed on the button
    return (
      document.activeElement === button ||
      button.contains(document.activeElement)
    );
  },
});
</script>

<style scoped lang="scss">
// Footer wrapper styles
.dialog__footer-wrapper {
  // Remove any default padding - rows manage their own spacing
  padding: 0 !important;
}

.dialog__footer {
  // Focus ring styling for footer buttons using primary color
  :deep(button) {
    &:focus {
      outline: 1px solid var(--primary-color) !important;
      outline-offset: 1px !important;
    }

    // Remove default browser focus styles completely
    &:focus-visible {
      outline: 1px solid var(--primary-color) !important;
      outline-offset: 1px !important;
    }
  }
}

// Focus styles for toggles - matching button focus ring style
.dialog__footer-wrapper {
  .footer-row-1 {
    // Target the modal-control-toggle wrapper and the button inside
    :deep(.modal-control-toggle) {
      // Target the ds-toggle button element directly
      .ds-toggle {
        &:focus {
          outline: 1px solid var(--primary-color) !important;
          outline-offset: 1px !important;
        }

        &:focus-visible {
          outline: 1px solid var(--primary-color) !important;
          outline-offset: 1px !important;
        }
      }
    }
  }
}
</style>
